import { Children, isValidElement, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { fitChipCount } from './buildingViewAnchors';
import styles from './mainView.module.css';

interface IChipRowProps {
  /** The room for the row, in its own (unscaled) px. */
  available: number;
  /** The building's chip scale: the row scales, never its cell, so the cell's
   *  hit area stays the slab's own size at any zoom. */
  scale: number;
  children: ReactNode;
}

/**
 * A floor's chips on one line — as many as fit its slab, the rest folded into
 * "+N" (the floor's hover card and drawer list them all). Every chip stays
 * mounted, the overflow ones out of flow and unseen, so the row re-measures
 * their natural widths whenever its chips or its room change.
 */
export function ChipRow({ available, scale, children }: IChipRowProps) {
  // Keyed elements only: each slot takes its chip's key, so a reordered row
  // moves the chips (and an open hover card) with their slots.
  const chips = Children.toArray(children).filter(isValidElement);
  const rowRef = useRef<HTMLSpanElement>(null);
  const moreRef = useRef<HTMLSpanElement>(null);
  const [shown, setShown] = useState(chips.length);

  // On every new set of chips, not just a new count: a chip's width changes
  // with its count or label. An unchanged result bails out of re-rendering.
  useLayoutEffect(() => {
    // Both spans always render, so both refs are attached by now.
    const slots = rowRef.current!.querySelectorAll<HTMLElement>(':scope > [data-chip-slot]');
    setShown(fitChipCount(Array.from(slots, (slot) => slot.offsetWidth), available, moreRef.current!.offsetWidth));
  }, [available, children]);

  const hidden = chips.length - shown;
  return (
    <span ref={rowRef} className={styles.chipRow} style={scale !== 1 ? { transform: `scale(${scale})` } : undefined}>
      {chips.map((chip, index) => (
        <span
          key={chip.key}
          data-chip-slot=""
          className={index < shown ? styles.chipSlot : `${styles.chipSlot} ${styles.chipOverflow}`}
        >
          {chip}
        </span>
      ))}
      <span ref={moreRef} className={hidden > 0 ? styles.chipMore : `${styles.chipMore} ${styles.chipOverflow}`}>
        +{Math.max(1, hidden)}
      </span>
    </span>
  );
}
